import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

// Collapsible mobile menu section
const MobileDropdown = ({ title, items, lang, navigate, close }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button className="flex items-center justify-between w-full text-[15px] font-semibold text-[#333] py-3 text-left uppercase tracking-wide"
        onClick={() => setOpen(!open)}>
        {title}
        <ChevronDown size={16} className={`text-[#484c55]/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-3 pl-2">
          {items.map((item) => (
            <button key={item.slug} className="text-[14px] text-[#555] py-2 text-left w-full hover:text-[#c30909] transition-colors"
              onClick={() => { navigate(`/info/${item.slug}`); close(); }}>
              {item.label[lang] || item.label.en}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const langRef = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { lang, setLang, t } = useLanguage();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === lang);

  const initials = user ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() : '';

  // Dropdown menus config
  const passengersMenu = [
    { label: { ru: 'Путешествие с домашними животными', en: 'Traveling with pets', lv: 'Ceļošana ar mājdzīvniekiem', lt: 'Kelionė su augintiniais', pl: 'Podróż ze zwierzętami' }, slug: 'faq-pets' },
    { label: { ru: 'Путешествие с несовершеннолетними', en: 'Traveling with minors', lv: 'Ceļošana ar nepilngadīgajiem', lt: 'Kelionė su nepilnamečiais', pl: 'Podróż z małoletnimi' }, slug: 'faq-minors' },
    { label: { ru: 'Как путешествовать с велосипедом?', en: 'How to travel with a bicycle?', lv: 'Kā ceļot ar velosipēdu?', lt: 'Kaip keliauti su dviračiu?', pl: 'Jak podróżować z rowerem?' }, slug: 'faq-bicycle' },
    { label: { ru: 'Часто задаваемые вопросы', en: 'FAQ', lv: 'Biežāk uzdotie jautājumi', lt: 'DUK', pl: 'FAQ' }, slug: 'faq' },
  ];

  const aboutMenu = [
    { label: { ru: 'О Lux Express', en: 'About Lux Express', lv: 'Par Lux Express', lt: 'Apie Lux Express', pl: 'O Lux Express' }, slug: 'about' },
    { label: { ru: 'Контакты', en: 'Contacts', lv: 'Kontakti', lt: 'Kontaktai', pl: 'Kontakt' }, slug: 'contacts' },
    { label: { ru: 'Карьера', en: 'Career', lv: 'Karjera', lt: 'Karjera', pl: 'Kariera' }, slug: 'career' },
    { label: { ru: 'Бизнес-сервис', en: 'Business service', lv: 'Biznesa serviss', lt: 'Verslo paslauga', pl: 'Usługi biznesowe' }, slug: 'business' },
  ];

  const infoMenu = [
    { label: { ru: 'Политика конфиденциальности', en: 'Privacy policy', lv: 'Privātuma politika', lt: 'Privatumo politika', pl: 'Polityka prywatności' }, slug: 'privacy' },
    { label: { ru: 'Правила продажи билетов', en: 'Ticket sales rules', lv: 'Biļešu tirdzniecības noteikumi', lt: 'Bilietų pardavimo taisyklės', pl: 'Zasady sprzedaży biletów' }, slug: 'terms' },
    { label: { ru: 'Таможенная служба', en: 'Customs', lv: 'Muita', lt: 'Muitinė', pl: 'Cło' }, slug: 'customs' },
    { label: { ru: 'Правила перевозки', en: 'Transport rules', lv: 'Pārvadāšanas noteikumi', lt: 'Vežimo taisyklės', pl: 'Zasady przewozu' }, slug: 'transport' },
  ];

  const DropdownMenu = ({ id, items }) => (
    openDropdown === id && (
      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border py-1 min-w-[260px] z-50">
        {items.map((item) => (
          <button
            key={item.slug}
            onClick={() => { navigate(`/info/${item.slug}`); setOpenDropdown(null); }}
            className="w-full text-left px-4 py-2.5 text-sm text-[#484c55] hover:bg-[#f0f0f1] hover:text-[#c30909] transition-colors"
          >
            {item.label[lang] || item.label.en}
          </button>
        ))}
      </div>
    )
  );

  const navTextClass = `text-[14px] font-semibold tracking-wide uppercase transition-colors hover:text-[#c30909] ${isHome ? 'text-white' : 'text-[#222]'}`;

  return (
    <>
      <header className={`w-full z-50 ${isHome ? 'absolute top-0 left-0' : 'bg-white shadow-sm'}`}>
        <div className="w-full px-3 md:pl-8 md:pr-8 flex items-center justify-between h-[56px] md:h-[72px]">
          <Link to="/" className="flex-shrink-0 mr-8 flex items-baseline gap-[1px] overflow-visible">
            <span className="leading-none inline-flex overflow-visible"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', transform: 'scaleX(0.6) skewX(-8deg)', transformOrigin: 'left bottom', paddingRight: '4px' }}>
              <span className={`text-[34px] md:text-[40px]`}
                style={{ letterSpacing: '-0.08em', color: isHome ? '#e8d9a8' : '#c30909' }}>L</span>
              <span className={`text-[34px] md:text-[40px]`}
                style={{ letterSpacing: '-0.06em', color: isHome ? '#e8d9a8' : '#c30909' }}>U</span>
              <span className={`text-[34px] md:text-[40px]`} style={{
                background: isHome
                  ? 'linear-gradient(90deg, #e8d9a8 35%, #c30909 100%)'
                  : 'linear-gradient(90deg, #c30909 35%, #8b0000 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>X</span>
            </span>
            <span
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontStyle: 'italic', fontSize: '21px', letterSpacing: '-0.03em', marginLeft: '-28px', marginBottom: '1px', lineHeight: 1, alignSelf: 'flex-end', color: isHome ? '#d0d0d0' : '#888', transform: 'scaleX(0.65) skewX(-6deg)', transformOrigin: 'left bottom' }}>Express</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-2 xl:gap-5" ref={dropdownRef}>
            {/* LUX EXPRESS SUPERIOR */}
            <button onClick={() => navigate('/info/superior')} className={navTextClass}>
              LUX EXPRESS SUPERIOR
            </button>

            {/* МАРШРУТЫ */}
            <button onClick={() => navigate('/info/routes')} className={navTextClass}>
              {t('nav.routes')}
            </button>

            {/* МОИ БРОНИРОВАНИЯ */}
            <button onClick={() => user ? navigate('/account') : setAuthOpen(true)} className={navTextClass}>
              {t('nav.myBookings')}
            </button>

            {/* ПАССАЖИРАМ ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'passengers' ? null : 'passengers')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.passengers')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'passengers' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="passengers" items={passengersMenu} />
            </div>

            {/* О НАС ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'about' ? null : 'about')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.about')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="about" items={aboutMenu} />
            </div>

            {/* ИНФОРМАЦИЯ ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'info' ? null : 'info')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.info')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'info' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="info" items={infoMenu} />
            </div>
          </nav>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Auth */}
            {user ? (
              <button
                onClick={() => navigate('/account')}
                className={`hidden md:flex items-center gap-2 text-xs font-semibold transition-colors ${isHome ? 'text-white' : 'text-[#484c55]'}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#c9a96e] flex items-center justify-center text-white text-xs font-bold">
                  {initials}
                </div>
              </button>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                className={`hidden md:flex items-center gap-1 text-[14px] font-semibold uppercase tracking-wide transition-colors hover:text-[#c30909] ${
                  isHome ? 'text-white' : 'text-[#222]'
                }`}
              >
                {t('auth.loginBtn')}
              </button>
            )}

            {/* Language — hidden on mobile, shown in slide menu */}
            <div className="relative hidden md:block" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1 cursor-pointer">
                <Globe size={16} className={isHome ? 'text-white' : 'text-[#484c55]'} />
                <span className={`text-xs font-semibold ${isHome ? 'text-white' : 'text-[#484c55]'}`}>{currentLang?.label}</span>
                <ChevronDown size={12} className={`transition-transform ${langOpen ? 'rotate-180' : ''} ${isHome ? 'text-white/60' : 'text-[#484c55]/40'}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border py-1 min-w-[160px] z-50">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#f0f0f1] transition-colors flex items-center justify-between ${lang === l.code ? 'text-[#c30909] font-semibold' : 'text-[#484c55]'}`}
                    >
                      <span>{l.name}</span>
                      <span className="text-xs text-[#484c55]/40 font-medium">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={24} className={isHome ? 'text-white' : 'text-[#484c55]'} /> : <Menu size={24} className={isHome ? 'text-white' : 'text-[#484c55]'} />}
            </button>
          </div>
        </div>

        {/* Mobile menu — full screen overlay from left */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-[100]" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-black/30" />
          </div>
        )}
        <div className={`lg:hidden fixed top-0 left-0 h-full w-full max-w-[400px] bg-white z-[101] transform transition-transform duration-300 overflow-y-auto ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {/* Header with logo and close */}
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-baseline gap-[1px]">
              <span className="font-extrabold italic text-[22px]" style={{ fontFamily: "'Montserrat', sans-serif", color: '#c30909' }}>LUX</span>
              <span className="text-[10px] font-medium italic text-[#888]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Express</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Globe size={14} className="text-[#484c55]" />
                <span className="text-xs font-semibold text-[#484c55]">{currentLang?.label}</span>
                <ChevronDown size={10} className="text-[#484c55]/40" />
              </div>
              <button onClick={() => setMobileOpen(false)}>
                <X size={24} className="text-[#484c55]" />
              </button>
            </div>
          </div>

          <nav className="flex flex-col px-5 py-4">
            {/* Auth */}
            <button className="text-[15px] font-semibold text-[#333] py-3 text-left border-b border-gray-100 uppercase tracking-wide"
              onClick={() => { user ? navigate('/account') : setAuthOpen(true); setMobileOpen(false); }}>
              {t('nav.login')}
            </button>

            {/* Language select */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-[15px] font-semibold text-[#333] uppercase tracking-wide">{lang === 'ru' ? 'ВЫБЕРИТЕ ЯЗЫК' : 'SELECT LANGUAGE'}</span>
              <select value={lang} onChange={(e) => setLang(e.target.value)}
                className="text-sm font-semibold text-[#484c55] bg-transparent border-none outline-none cursor-pointer">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>

            {/* Superior */}
            <button className="text-[15px] font-semibold text-[#c30909] py-3 text-left border-b border-gray-100 uppercase tracking-wide"
              onClick={() => { navigate('/info/superior'); setMobileOpen(false); }}>
              LUX EXPRESS SUPERIOR
            </button>

            {/* Routes */}
            <button className="text-[15px] font-semibold text-[#333] py-3 text-left border-b border-gray-100 uppercase tracking-wide"
              onClick={() => { navigate('/info/routes'); setMobileOpen(false); }}>
              {t('nav.routes')}
            </button>

            {/* Passengers — collapsible */}
            <MobileDropdown title={t('nav.passengers')} items={passengersMenu} lang={lang} navigate={navigate} close={() => setMobileOpen(false)} />

            {/* My Bookings — collapsible */}
            <button className="text-[15px] font-semibold text-[#333] py-3 text-left border-b border-gray-100 uppercase tracking-wide"
              onClick={() => { user ? navigate('/account') : setAuthOpen(true); setMobileOpen(false); }}>
              {t('nav.myBookings')}
            </button>

            {/* About — collapsible */}
            <MobileDropdown title={t('nav.about')} items={aboutMenu} lang={lang} navigate={navigate} close={() => setMobileOpen(false)} />

            {/* Info — collapsible */}
            <MobileDropdown title={t('nav.info')} items={infoMenu} lang={lang} navigate={navigate} close={() => setMobileOpen(false)} />

            {/* Back to home */}
            <button className="flex items-center gap-2 text-[14px] text-[#c30909] font-medium py-4 mt-4"
              onClick={() => { navigate('/'); setMobileOpen(false); }}>
              ← {lang === 'ru' ? 'На главную' : 'Home'}
            </button>
          </nav>
        </div>

        {/* Legacy mobile menu placeholder for closing tag */}
        {false && (
          <div>
            <nav className="flex flex-col p-4 gap-1">
              <button className="text-sm font-semibold text-[#c30909] border border-[#c30909] rounded-full px-4 py-2 mt-2">
                  {t('auth.loginBtn')}
                </button>
              )}
            </nav>
          </div>
        )}
      </header>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};

export default Header;
